import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { FetchResult } from '@apollo/client/link/core';
import { GqlError, GqlResponse } from 'utils/gql/gql.types';
import { GqlResponseError } from 'utils/gql/errors/GqlResponseError';
import { ServerParseError } from '@apollo/client/link/http';
import { ServerError } from '@apollo/client/link/utils';

export function toGqlResponse<DATA>(
  result: ApolloQueryResult<DATA> | FetchResult<DATA> | ApolloError,
  operationName: string
): GqlResponse<DATA> {
  const isError = isApolloError(result);
  const status = isError ? getStatus(result.networkError) : 200;
  const errors = isError ? (result.graphQLErrors as GqlError[]) : (result?.errors as GqlError[]);
  const clientError = errors && errors.length ? new GqlResponseError(errors) : undefined;
  const data = !isError ? result?.data : undefined;
  return {
    data,
    errors: clientError,
    isError: isApolloError(result),
    response: {
      data: {
        data,
        errors: clientError
      },
      status,
      statusText: clientError?.message
    },
    onSuccess(callback: (data: DATA) => void) {
      if (data) {
        callback(data);
      }
    },
    onError(callback: (e: GqlResponseError) => void) {
      if (clientError) {
        callback(clientError);
      }
    },
    operationName
  };
}

function getStatus(error: Error | ServerParseError | ServerError | null): number | undefined {
  if (error && 'statusCode' in error) {
    return error.statusCode;
  }
  return undefined;
}

export function isApolloError<T>(
  result: ApolloQueryResult<T> | FetchResult<T> | ApolloError | unknown
): result is ApolloError {
  if (typeof result !== 'object' || result === null) {
    return false;
  }
  return 'graphQLErrors' in result;
}
